import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, FileText, ArrowRight, Sparkles } from 'lucide-react';
import blogMetadata from '../data/blogMetadata.json';
import { getAllArticles } from '../knowledge/knowledgeArticles';
import { getLocalizedSlug } from '../blog/slugMapping';
import { getKnowledgeArticleUrlSync } from '../knowledge/slugMapping';
import {
  blogPostToContentItem,
  knowledgeArticleToContentItem,
  findTopRelevantContent,
  KNOWLEDGE_ARTICLE_KEYWORDS,
  PSEO_CONTENT_MAPPINGS,
  ContentItem,
} from '../utils/contentRelevance';

interface RelatedContentForPSEOProps {
  pageId: string; // e.g., 'chatgpt-watermark', 'gemini-watermark'
  pageTitle: string;
  pageDescription: string;
  locale: string;
}

/**
 * RelatedContentForPSEO Component
 *
 * Displays 2 Knowledge Articles + 2 Blog Posts on pSEO pages.
 * Inserts before FAQ section or at the bottom.
 *
 * Features:
 * - Automatic relevance matching with manual overrides for key pages
 * - Localized titles and slugs
 * - SSR-compatible
 * - Canonical-correct URLs
 * - SEO-optimized section headings
 */
export const RelatedContentForPSEO: React.FC<RelatedContentForPSEOProps> = ({
  pageId,
  pageTitle,
  pageDescription,
  locale,
}) => {
  // Get all content from static metadata
  const blogPosts = useMemo(() => {
    return blogMetadata[locale as keyof typeof blogMetadata] || blogMetadata.en;
  }, [locale]);
  const knowledgeArticles = useMemo(() => getAllArticles(locale), [locale]);

  // State for localized blog slugs
  const [localizedBlogSlugs, setLocalizedBlogSlugs] = React.useState<Record<string, string>>({});

  // Get related knowledge articles and blog posts
  const { relatedKnowledge, relatedBlogs } = useMemo(() => {
    // Check if we have manual mappings for this page
    const manualMapping = PSEO_CONTENT_MAPPINGS[pageId];

    let knowledgeSlugs: string[];
    let blogSlugs: string[];

    if (manualMapping) {
      // Use manual mappings for important pages
      knowledgeSlugs = manualMapping.knowledgeArticles.slice(0, 2);
      blogSlugs = manualMapping.blogPosts.slice(0, 2);
    } else {
      // Use automatic relevance matching
      const sourceContent: ContentItem = {
        slug: pageId,
        title: pageTitle,
        description: pageDescription,
        keywords: pageTitle.toLowerCase().split(/\s+/),
        tags: [],
        category: undefined,
      };

      // Find relevant knowledge articles
      const knowledgeCandidates = knowledgeArticles.map(article =>
        knowledgeArticleToContentItem(
          article,
          KNOWLEDGE_ARTICLE_KEYWORDS[article.slug] || []
        )
      );

      const topKnowledge = findTopRelevantContent(sourceContent, knowledgeCandidates, {
        limit: 2,
        minScore: 5,
      });

      knowledgeSlugs = topKnowledge.map(k => k.slug);

      // Find relevant blog posts
      const blogCandidates = blogPosts.map(post => blogPostToContentItem(post));

      const topBlogs = findTopRelevantContent(sourceContent, blogCandidates, {
        limit: 2,
        minScore: 5,
      });

      blogSlugs = topBlogs.map(b => b.slug);
    }

    // Map slugs to full objects
    const relatedKnowledge = knowledgeSlugs
      .map(slug => knowledgeArticles.find(a => a.slug === slug))
      .filter(Boolean);

    const relatedBlogs = blogSlugs
      .map(slug => blogPosts.find(p => p.slug === slug))
      .filter(Boolean);

    return { relatedKnowledge, relatedBlogs };
  }, [pageId, pageTitle, pageDescription, knowledgeArticles, blogPosts]);

  // Load localized slugs for blog posts
  React.useEffect(() => {
    if (!relatedBlogs.length) return;

    const loadSlugs = async () => {
      const slugMap: Record<string, string> = {};
      for (const blog of relatedBlogs) {
        if (blog) {
          const localizedSlug = await getLocalizedSlug(blog.slug, locale);
          slugMap[blog.slug] = localizedSlug;
        }
      }
      setLocalizedBlogSlugs(slugMap);
    };

    loadSlugs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locale, pageId, pageTitle, pageDescription]);

  // Don't render if no content found
  if (relatedKnowledge.length === 0 && relatedBlogs.length === 0) {
    return null;
  }

  // Get localized blog URL
  const getBlogUrl = (englishSlug: string): string => {
    const localizedSlug = localizedBlogSlugs[englishSlug] || englishSlug;
    if (locale === 'en') return `/blog/${localizedSlug}`;
    const prefix = locale === 'pt-BR' ? 'pt-br' : locale === 'ms-MY' ? 'my' : locale === 'es' ? 'es' : locale;
    return `/${prefix}/blog/${localizedSlug}`;
  };

  // Get localized text
  const getText = (key: string): string => {
    const translations: Record<string, Record<string, string>> = {
      heading: {
        en: 'Learn More',
        de: 'Mehr erfahren',
        'pt-BR': 'Saiba Mais',
        'ms-MY': 'Ketahui Lebih Lanjut',
        es: 'Más Información',
        fr: 'En Savoir Plus',
        ja: 'もっと詳しく',
        id: 'Pelajari Lebih Lanjut',
      },
      knowledgeHeading: {
        en: 'In-Depth Guides',
        de: 'Ausführliche Anleitungen',
        'pt-BR': 'Guias Detalhados',
        'ms-MY': 'Panduan Mendalam',
        es: 'Guías Detalladas',
        fr: 'Guides Approfondis',
        ja: '詳細ガイド',
        id: 'Panduan Mendalam',
      },
      blogHeading: {
        en: 'Related Articles',
        de: 'Verwandte Artikel',
        'pt-BR': 'Artigos Relacionados',
        'ms-MY': 'Artikel Berkaitan',
        es: 'Artículos Relacionados',
        fr: 'Articles Connexes',
        ja: '関連記事',
        id: 'Artikel Terkait',
      },
      readMore: {
        en: 'Read more',
        de: 'Mehr lesen',
        'pt-BR': 'Leia mais',
        'ms-MY': 'Baca lebih lanjut',
        es: 'Leer más',
        fr: 'Lire la suite',
        ja: '続きを読む',
        id: 'Baca selengkapnya',
      },
    };
    return translations[key]?.[locale] || translations[key]?.['en'] || '';
  };

  return (
    <section className="my-16 py-12 bg-neutral-50 dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800">
      <div className="max-w-7xl mx-auto px-6">
        {/* Main Heading */}
        <div className="flex items-center gap-3 mb-10 justify-center">
          <Sparkles className="w-7 h-7 text-blue-600 dark:text-blue-400" />
          <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white text-center">
            {getText('heading')}
          </h2>
          <Sparkles className="w-7 h-7 text-blue-600 dark:text-blue-400" />
        </div>

        {/* Knowledge Articles Section */}
        {relatedKnowledge.length > 0 && (
          <div className="mb-12">
            <div className="flex items-center gap-2 mb-6">
              <BookOpen className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h3 className="text-xl font-semibold text-neutral-900 dark:text-white">
                {getText('knowledgeHeading')}
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {relatedKnowledge.map((article) => {
                if (!article) return null;

                const articleUrl = getKnowledgeArticleUrlSync(article.slug, locale);

                return (
                  <Link
                    key={article.slug}
                    to={articleUrl}
                    className="group bg-white dark:bg-neutral-950 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-6 border border-neutral-200 dark:border-neutral-800 hover:border-blue-500 dark:hover:border-blue-500"
                  >
                    {/* Icon */}
                    <div className="w-12 h-12 bg-blue-600 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                      <BookOpen className="w-6 h-6 text-white" />
                    </div>

                    {/* Title */}
                    <h4 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-2">
                      {article.title}
                    </h4>

                    {/* Description */}
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4 line-clamp-3">
                      {article.metaDescription}
                    </p>

                    {/* Read More - Unique per article */}
                    <span className="flex items-center gap-2 text-blue-600 dark:text-blue-400 text-sm font-medium group-hover:gap-3 transition-all">
                      <span>{getText('readMore')}: {article.title.length > 25 ? article.title.substring(0, 25) + '...' : article.title}</span>
                      <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Blog Posts Section */}
        {relatedBlogs.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-6">
              <FileText className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              <h3 className="text-xl font-semibold text-neutral-900 dark:text-white">
                {getText('blogHeading')}
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {relatedBlogs.map((post) => {
                if (!post) return null;

                return (
                  <Link
                    key={post.slug}
                    to={getBlogUrl(post.slug)}
                    className="group bg-white dark:bg-neutral-950 rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden border border-neutral-200 dark:border-neutral-800 hover:border-blue-500 dark:hover:border-blue-500"
                  >
                    {/* Cover Image */}
                    {post.coverImage && (
                      <div className="aspect-video overflow-hidden bg-neutral-100 dark:bg-neutral-900">
                        <img
                          src={post.coverImage}
                          alt={post.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          loading="lazy"
                        />
                      </div>
                    )}

                    {/* Content */}
                    <div className="p-5">
                      {/* Title */}
                      <h4 className="text-lg font-semibold text-neutral-900 dark:text-white mb-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors line-clamp-2">
                        {post.title}
                      </h4>

                      {/* Description */}
                      <p className="text-sm text-neutral-600 dark:text-neutral-400 mb-4 line-clamp-2">
                        {post.description}
                      </p>

                      {/* Read More - Unique per post */}
                      <span className="flex items-center gap-2 text-blue-600 dark:text-blue-400 text-sm font-medium group-hover:gap-3 transition-all">
                        <span>{getText('readMore')}: {post.title.length > 25 ? post.title.substring(0, 25) + '...' : post.title}</span>
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default RelatedContentForPSEO;
